import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, TrendingDown, ShoppingBag, Mail, CreditCard, BarChart3,
  LogOut, RefreshCw, Search, Loader2, Activity, Filter, DollarSign,
  Users, ArrowUpRight, ArrowDownRight, Sparkles, Download,
  Clock, Globe, Trash2, Send, Plus, Pause, Play, CheckCircle2, XCircle,
} from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALETTE = ["#10b981", "#34d399", "#a7f3d0", "#fbbf24", "#fca5a5", "#cbd5e1"];

// -- Shared 401 handler (post-code-review 2026-07-08) --
// Every sub-panel (LeadsTable, TransactionsTable, ShopsTable, briefing CRUD)
// now goes through this authedAxios() and inherits a single response
// interceptor: if the aigo_admin cookie is missing/expired the axios call
// returns 401 → we fire onUnauthorized() → AdminDashboard.logout() runs →
// user lands on /admin/login. Without this, sub-panels would silently
// present stale data after a 24h token lapse.
let onUnauthorized = null;
let unauthorizedFired = false;

const authedAxios = () => {
  const instance = axios.create({
    baseURL: API,
    withCredentials: true,   // send the HttpOnly `aigo_admin` cookie on every call
  });
  instance.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err.response?.status === 401 && !unauthorizedFired && typeof onUnauthorized === "function") {
        unauthorizedFired = true;
        // Debounce so a burst of parallel 401s from Promise.all doesn't
        // re-navigate 6x. Cleared shortly after navigate completes.
        setTimeout(() => { unauthorizedFired = false; }, 1000);
        onUnauthorized();
      }
      return Promise.reject(err);
    },
  );
  return instance;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [sources, setSources] = useState([]);
  const [planBreakdown, setPlanBreakdown] = useState([]);
  const [activity, setActivity] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const logout = useCallback(async () => {
    // Ask the backend to clear the HttpOnly cookie. Soft-fail: even if the
    // network call errors (including 401 — session already dead), we still
    // redirect to login. The interceptor debounce prevents this
    // logout()→POST→401→onUnauthorized→logout() loop from re-firing.
    try {
      await authedAxios().post("/auth/logout");
    } catch (_) {
      /* noop */
    }
    localStorage.removeItem("aigo_admin_email");
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  // Register logout() as the shared 401 handler for the lifetime of the
  // mounted dashboard. Unregister on unmount so no other page inherits it.
  useEffect(() => {
    onUnauthorized = logout;
    return () => { onUnauthorized = null; };
  }, [logout]);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const ax = authedAxios();
      const [meRes, overRes, tsRes, srcRes, planRes, actRes] = await Promise.all([
        ax.get("/auth/me"),
        ax.get("/admin/overview"),
        ax.get("/admin/timeseries?days=30"),
        ax.get("/admin/sources"),
        ax.get("/admin/plan-breakdown"),
        ax.get("/admin/activity?limit=20"),
      ]);
      setMe(meRes.data);
      setOverview(overRes.data);
      setTimeseries(tsRes.data.series || []);
      setSources(srcRes.data.sources || []);
      setPlanBreakdown(planRes.data.plans || []);
      setActivity(actRes.data.activity || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [logout]);

  useEffect(() => {
    // Cookie-based session: we can't inspect the HttpOnly cookie from JS,
    // so we let /auth/me be the source of truth. A 401 triggers logout()
    // → redirect to /admin/login.
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-root"
         className="min-h-screen bg-slate-50 text-slate-900 antialiased"
         style={{ fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" />
      <style>{`
        @keyframes admin-rise { 0% { opacity: 0; transform: translateY(8px);} 100% { opacity: 1; transform: translateY(0);} }
        .admin-rise { animation: admin-rise 360ms cubic-bezier(.2,.8,.2,1) both; }
      `}</style>

      <Topbar me={me} onRefresh={fetchAll} refreshing={refreshing} onLogout={logout} />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-8">
        <div className="admin-rise">
          <OverviewKpis overview={overview} />
        </div>
        <div className="admin-rise" style={{ animationDelay: "60ms" }}>
          <FunnelStrip overview={overview} />
        </div>
        <div className="grid lg:grid-cols-3 gap-5 admin-rise" style={{ animationDelay: "120ms" }}>
          <div className="lg:col-span-2"><TimeseriesChart series={timeseries} /></div>
          <SourcesPie sources={sources} />
        </div>
        <div className="grid lg:grid-cols-3 gap-5 admin-rise" style={{ animationDelay: "180ms" }}>
          <PlanBreakdown plans={planBreakdown} />
          <div className="lg:col-span-2"><ActivityFeed activity={activity} /></div>
        </div>
        <div className="admin-rise" style={{ animationDelay: "240ms" }}>
          <LeadsTable />
        </div>
        <div className="admin-rise" style={{ animationDelay: "280ms" }}>
          <TransactionsTable />
        </div>
        <div className="admin-rise" style={{ animationDelay: "320ms" }}>
          <ShopsTable />
        </div>
        <div className="admin-rise" style={{ animationDelay: "360ms" }}>
          <BriefingSubscribersPanel />
        </div>
      </main>
    </div>
  );
};

/* === Topbar === */
const Topbar = ({ me, onRefresh, refreshing, onLogout }) => (
  <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm shadow-emerald-500/5">
    <div className="max-w-7xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3 group">
        <img src="/logo.png" alt="AI Growth Operator" className="w-8 h-8 rounded-lg object-contain group-hover:scale-105 transition" />
        <div>
          <div className="text-sm font-bold tracking-tight">AI Growth Operator</div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Admin Console</div>
        </div>
      </Link>
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-500 max-w-[200px] truncate">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {me?.email}
        </span>
        <button data-testid="admin-refresh" onClick={onRefresh} disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/40 text-slate-700 text-xs font-semibold disabled:opacity-60 transition">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button data-testid="admin-logout" onClick={onLogout}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition">
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </div>
    </div>
    {/* hairline emerald gradient under header */}
    <div className="h-px bg-gradient-to-r from-transparent via-emerald-300/40 to-transparent" />
  </header>
);

/* === Overview KPI cards === */
const OverviewKpis = ({ overview }) => {
  if (!overview) return null;
  const cards = [
    { icon: <DollarSign className="w-5 h-5" />, label: "Revenue · 7d", value: `$${overview.transactions.revenue_usd_7d.toFixed(2)}`, sub: `$${overview.transactions.revenue_usd_total.toFixed(2)} all-time`, tone: "emerald", testid: "kpi-revenue" },
    { icon: <CreditCard className="w-5 h-5" />, label: "MRR projection", value: `$${overview.transactions.mrr_usd.toFixed(2)}`, sub: `${overview.transactions.paid} paid · ${overview.transactions.total} attempted`, tone: "emerald", testid: "kpi-mrr" },
    { icon: <Users className="w-5 h-5" />, label: "Leads · 7d", value: overview.leads.last_7d, sub: `${overview.leads.total} total · ${overview.leads.delivered} delivered`, delta: overview.leads.delta_pct_7d, testid: "kpi-leads" },
    { icon: <ShoppingBag className="w-5 h-5" />, label: "Shopify installs", value: overview.shops.total, sub: `${overview.shops.last_7d} in last 7d`, testid: "kpi-shops" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testid}
             className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/10 hover:-translate-y-0.5 transition">
          <div className="flex items-start justify-between">
            <div className={`w-9 h-9 rounded-lg border flex items-center justify-center transition group-hover:scale-105 ${
              c.tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-slate-50 text-slate-600"
            }`}>{c.icon}</div>
            {c.delta != null && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${c.delta >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                {c.delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {Math.abs(c.delta).toFixed(1)}%
              </span>
            )}
          </div>
          <div className="text-2xl md:text-3xl font-extrabold tabular-nums tracking-tight mt-4">{c.value}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mt-1">{c.label}</div>
          <div className="text-[11px] text-slate-500 mt-1.5">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

/* === Funnel strip === */
const FunnelStrip = ({ overview }) => {
  if (!overview) return null;
  const f = overview.funnel;
  const max = Math.max(f.leads, f.checkout_started, f.paid, 1);
  const stages = [
    { label: "Visits", n: "n/a", pct: 100, note: "Connect analytics", muted: true },
    { label: "Leads", n: f.leads, pct: 100 },
    { label: "Checkouts", n: f.checkout_started, pct: (f.checkout_started / max) * 100, conv: f.lead_to_checkout_pct },
    { label: "Paid", n: f.paid, pct: (f.paid / max) * 100, conv: f.checkout_to_paid_pct },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-funnel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Conversion funnel</div>
          <h3 className="text-lg font-bold tracking-tight mt-1">All-time pipeline</h3>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {stages.map((s, i) => (
          <div key={s.label}>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500">{s.label}</div>
            <div className={`mt-1 text-2xl font-extrabold tabular-nums ${s.muted ? "text-slate-300" : "text-slate-900"}`}>{s.n}</div>
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{
                width: `${Math.max(s.pct, 4)}%`,
                background: s.muted ? "#cbd5e1" : "linear-gradient(90deg,#34d399,#10b981)",
              }} />
            </div>
            {s.conv != null && (
              <div className="mt-1.5 text-[10px] font-semibold text-emerald-700">{s.conv}% from prior</div>
            )}
            {s.muted && <div className="mt-1.5 text-[10px] text-slate-400">{s.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

/* === Timeseries area chart === */
const TimeseriesChart = ({ series }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-timeseries">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Last 30 days</div>
        <h3 className="text-lg font-bold tracking-tight mt-1">Leads · Checkouts · Revenue</h3>
      </div>
      <Activity className="w-4 h-4 text-slate-400" />
    </div>
    <div className="h-72">
      <ResponsiveContainer>
        <AreaChart data={series}>
          <defs>
            <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="checkoutGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
          <Area type="monotone" dataKey="leads" stroke="#10b981" fill="url(#leadsGrad)" strokeWidth={2} />
          <Area type="monotone" dataKey="checkouts" stroke="#34d399" fill="url(#checkoutGrad)" strokeWidth={2} />
          <Line type="monotone" dataKey="revenue" stroke="#0f172a" strokeWidth={1.5} dot={false} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

/* === Sources pie === */
const SourcesPie = ({ sources }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-sources">
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Acquisition</div>
      <h3 className="text-lg font-bold tracking-tight mt-1">Lead sources</h3>
    </div>
    <div className="h-60">
      <ResponsiveContainer>
        <PieChart>
          <Pie data={sources} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
            {sources.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
);

/* === Plan breakdown === */
const PlanBreakdown = ({ plans }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-plans">
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Revenue split</div>
      <h3 className="text-lg font-bold tracking-tight mt-1">By plan</h3>
    </div>
    {plans.length === 0 ? (
      <div className="text-sm text-slate-400 italic py-8 text-center">No transactions yet.</div>
    ) : (
      <div className="space-y-3">
        {plans.map((p) => (
          <div key={p.plan} className="flex items-center justify-between text-sm">
            <span className="font-bold text-slate-900 capitalize">{p.plan}</span>
            <div className="text-right">
              <div className="font-bold text-emerald-700 tabular-nums">${p.revenue.toFixed(2)}</div>
              <div className="text-[10px] text-slate-500">{p.paid} / {p.count} paid</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* === Activity feed === */
const ActivityFeed = ({ activity }) => {
  const iconFor = (t) =>
    t === "lead" ? <Mail className="w-3.5 h-3.5" /> :
    t === "checkout" ? <CreditCard className="w-3.5 h-3.5" /> :
    t === "shop" ? <ShoppingBag className="w-3.5 h-3.5" /> :
    <Activity className="w-3.5 h-3.5" />;
  const toneFor = (t) =>
    t === "lead" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    t === "checkout" ? "bg-amber-50 text-amber-700 border-amber-200" :
    t === "shop" ? "bg-sky-50 text-sky-700 border-sky-200" :
    "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-activity">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Live</div>
        <h3 className="text-lg font-bold tracking-tight mt-1">Activity feed</h3>
      </div>
      <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
        {activity.length === 0 && <div className="text-sm text-slate-400 italic py-4">No events yet.</div>}
        {activity.map((a, i) => (
          <div key={i} className="py-2.5 flex items-start gap-3">
            <span className={`inline-flex w-7 h-7 rounded-full border items-center justify-center shrink-0 ${toneFor(a.type)}`}>{iconFor(a.type)}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 truncate">{a.title}</div>
              <div className="text-[11px] text-slate-500 font-mono">{a.ts}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* === Leads table === */
const LeadsTable = () => {
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, leads: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.get(`/admin/leads?limit=25&skip=${skip}&q=${encodeURIComponent(q)}`);
      setData(data);
    } finally { setLoading(false); }
  }, [q, skip]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = [["email", "source", "email_sent", "email_id", "created_at"]];
    data.leads.forEach((l) => rows.push([l.email, l.source || "", l.email_sent, l.email_id || "", l.created_at || ""]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `leads-${Date.now()}.csv`; a.click();
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-leads-table">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Email captures</div>
          <h3 className="text-lg font-bold tracking-tight mt-1">Leads <span className="text-slate-400 text-sm font-medium">· {data.total} total</span></h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-300 bg-white">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input data-testid="admin-leads-search" type="search" value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }}
              placeholder="search email…" className="bg-transparent outline-none text-sm text-slate-900 placeholder-slate-400 w-40" />
          </div>
          <button onClick={exportCsv} data-testid="admin-leads-export"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 hover:border-emerald-400 hover:text-emerald-700 text-slate-700 text-xs font-semibold transition">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>
      <DataTable
        columns={[
          { key: "email", label: "Email" },
          { key: "source", label: "Source" },
          { key: "email_sent", label: "Delivered", render: (v) => v ? <Badge tone="emerald">✓ sent</Badge> : <Badge tone="slate">pending</Badge> },
          { key: "created_at", label: "Created", mono: true },
        ]}
        rows={data.leads} loading={loading}
      />
      <Pagination total={data.total} skip={skip} limit={25} onChange={setSkip} />
    </div>
  );
};

/* === Transactions table === */
const TransactionsTable = () => {
  const [plan, setPlan] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, transactions: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.get(`/admin/transactions?limit=25&skip=${skip}&plan=${plan}&payment_status=${paymentStatus}`);
      setData(data);
    } finally { setLoading(false); }
  }, [plan, paymentStatus, skip]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-transactions-table">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Payments</div>
          <h3 className="text-lg font-bold tracking-tight mt-1">Transactions <span className="text-slate-400 text-sm font-medium">· {data.total} total</span></h3>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="admin-tx-plan" value={plan} onChange={(e) => { setSkip(0); setPlan(e.target.value); }}
                  className="px-3 py-1.5 rounded-full border border-slate-300 bg-white text-xs font-semibold text-slate-700">
            <option value="">All plans</option>
            <option value="basic">Basic</option>
            <option value="operator">Operator</option>
          </select>
          <select data-testid="admin-tx-status" value={paymentStatus} onChange={(e) => { setSkip(0); setPaymentStatus(e.target.value); }}
                  className="px-3 py-1.5 rounded-full border border-slate-300 bg-white text-xs font-semibold text-slate-700">
            <option value="">All statuses</option>
            <option value="initiated">Initiated</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>
      <DataTable
        columns={[
          { key: "session_id", label: "Session", render: (v) => <span className="font-mono text-[11px] text-slate-500">{(v || "").slice(0, 16)}…</span> },
          { key: "plan", label: "Plan", render: (v) => <span className="capitalize font-semibold">{v}</span> },
          { key: "amount", label: "Amount", render: (v, r) => `$${Number(v || 0).toFixed(2)} ${(r.currency || "").toUpperCase()}` },
          { key: "payment_status", label: "Status", render: (v) => <Badge tone={v === "paid" ? "emerald" : v === "expired" ? "rose" : "amber"}>{v}</Badge> },
          { key: "created_at", label: "Created", mono: true },
        ]}
        rows={data.transactions} loading={loading}
      />
      <Pagination total={data.total} skip={skip} limit={25} onChange={setSkip} />
    </div>
  );
};

/* === Shops table === */
const ShopsTable = () => {
  const [data, setData] = useState({ total: 0, shops: [] });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await authedAxios().get("/admin/shops"); setData(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-shops-table">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Shopify</div>
        <h3 className="text-lg font-bold tracking-tight mt-1">Installs <span className="text-slate-400 text-sm font-medium">· {data.total} total</span></h3>
      </div>
      <DataTable
        columns={[
          { key: "shop", label: "Shop" },
          { key: "scope", label: "Scope", render: (v) => <span className="font-mono text-[10px] text-slate-500">{v}</span> },
          { key: "installed_at", label: "Installed", mono: true },
        ]}
        rows={data.shops} loading={loading}
      />
    </div>
  );
};

/* === Generic helpers === */
const DataTable = ({ columns, rows, loading }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500 border-b border-slate-200">
          {columns.map((c) => <th key={c.key} className="py-2 pr-4">{c.label}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {loading && (<tr><td colSpan={columns.length} className="py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>)}
        {!loading && rows.length === 0 && (<tr><td colSpan={columns.length} className="py-6 text-center text-slate-400 italic">No rows.</td></tr>)}
        {!loading && rows.map((r, i) => (
          <tr key={r.id || r.session_id || r.shop || r.email || i} className="hover:bg-slate-50">
            {columns.map((c) => (
              <td key={c.key} className={`py-2 pr-4 ${c.mono ? "font-mono text-[11px] text-slate-500" : "text-slate-700"}`}>
                {c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Badge = ({ tone = "slate", children }) => {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
    sky:     "bg-sky-50 border-sky-200 text-sky-700",
    slate:   "bg-slate-50 border-slate-200 text-slate-600",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.08em] ${map[tone]}`}>{children}</span>
  );
};

const Pagination = ({ total, skip, limit, onChange }) => {
  const page = Math.floor(skip / limit) + 1;
  const lastPage = Math.max(1, Math.ceil(total / limit));
  if (total <= limit) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
      <span>Page {page} of {lastPage}</span>
      <div className="flex gap-2">
        <button onClick={() => onChange(Math.max(0, skip - limit))} disabled={skip === 0}
                className="px-3 py-1 rounded-full border border-slate-300 text-slate-700 disabled:opacity-50 hover:border-emerald-400 transition">
          ← Prev
        </button>
        <button onClick={() => onChange(skip + limit)} disabled={skip + limit >= total}
                className="px-3 py-1 rounded-full border border-slate-300 text-slate-700 disabled:opacity-50 hover:border-emerald-400 transition">
          Next →
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;

/* === Briefing subscribers panel === */
const BriefingSubscribersPanel = () => {
  const [subs, setSubs] = useState([]);
  const [tzList, setTzList] = useState(["UTC"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", shop: "", send_hour: 7, timezone: "UTC" });
  const [error, setError] = useState("");
  const [testStatus, setTestStatus] = useState({});
  const [sendAllStatus, setSendAllStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const [subsRes, tzRes] = await Promise.all([
        ax.get("/admin/briefing/subscribers"),
        ax.get("/admin/briefing/timezones"),
      ]);
      setSubs(subsRes.data.subscribers || []);
      setTzList(tzRes.data.timezones || ["UTC"]);
    } catch {
      // swallow — handled by global axios error layer
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subscribe = async () => {
    setError("");
    if (!form.email) return setError("Email is required");
    setSaving(true);
    try {
      const ax = authedAxios();
      await ax.post("/admin/briefing/subscribe", form);
      setForm({ email: "", shop: "", send_hour: 7, timezone: form.timezone });
      load();
    } catch (e) {
      setError(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Failed to subscribe");
    } finally {
      setSaving(false);
    }
  };

  const updateSub = async (id, patch) => {
    try {
      const ax = authedAxios();
      await ax.put(`/admin/briefing/subscribers/${id}`, patch);
      load();
    } catch (e) {
      // no-op
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Remove this briefing subscriber?")) return;
    const ax = authedAxios();
    await ax.delete(`/admin/briefing/subscribers/${id}`);
    load();
  };

  const sendTest = async (sub) => {
    setTestStatus((s) => ({ ...s, [sub.id]: "sending" }));
    try {
      const ax = authedAxios();
      const r = await ax.post("/admin/briefing/test-send", {
        email_override: sub.email,
        shop: sub.shop || undefined,
        timezone: sub.timezone || undefined,
      });
      setTestStatus((s) => ({ ...s, [sub.id]: r.data.sent ? "sent" : `failed: ${r.data.reason || "unknown"}` }));
      setTimeout(load, 800);
    } catch {
      setTestStatus((s) => ({ ...s, [sub.id]: "error" }));
    }
  };

  const sendAllNow = async () => {
    if (!window.confirm("Fire today's briefing to ALL active subscribers right now?")) return;
    setSendAllStatus({ loading: true });
    try {
      const ax = authedAxios();
      const r = await ax.post("/admin/briefing/send-all-now", {});
      setSendAllStatus({ loading: false, sent: r.data.sent, failed: r.data.failed });
      setTimeout(load, 1000);
      // Auto-clear status after 6s
      setTimeout(() => setSendAllStatus(null), 6000);
    } catch (e) {
      setSendAllStatus({ loading: false, error: "Send failed — see logs." });
      setTimeout(() => setSendAllStatus(null), 6000);
    }
  };

  return (
    <section data-testid="briefing-panel" className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-emerald-700 font-bold flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Daily 7 AM Profit Briefing
          </div>
          <h2 className="text-lg font-bold text-slate-900 mt-1">Subscribers</h2>
          <p className="text-xs text-slate-500 mt-0.5">Emails fire at <b>send hour</b> in each subscriber’s <b>local timezone</b>. Briefing pulls yesterday’s Shopify orders + Google Ads ROAS.</p>
        </div>
        <div className="flex items-center gap-2">
          {sendAllStatus && (
            <span data-testid="briefing-sendall-status"
                  className="text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1.5">
              {sendAllStatus.loading
                ? <><Loader2 className="w-3 h-3 animate-spin" /> sending all…</>
                : sendAllStatus.error
                  ? <><XCircle className="w-3 h-3 text-rose-500" /> {sendAllStatus.error}</>
                  : <><CheckCircle2 className="w-3 h-3" /> sent {sendAllStatus.sent}{sendAllStatus.failed ? ` · ${sendAllStatus.failed} failed` : ""}</>}
            </span>
          )}
          <button data-testid="briefing-send-all" onClick={sendAllNow} disabled={sendAllStatus?.loading || subs.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition shadow-sm shadow-emerald-500/30">
            <Send className="w-3.5 h-3.5" /> Send today’s briefing now
          </button>
          <button data-testid="briefing-refresh" onClick={load}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 hover:border-emerald-400 hover:text-emerald-700 text-slate-700 text-xs font-semibold transition">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Reload
          </button>
        </div>
      </div>

      {/* Add form */}
      <div className="grid sm:grid-cols-12 gap-2 mb-5">
        <input data-testid="briefing-input-email" type="email" placeholder="ops@store.com"
               value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
               className="sm:col-span-4 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-emerald-500" />
        <input data-testid="briefing-input-shop" type="text" placeholder="shop.myshopify.com (optional)"
               value={form.shop} onChange={(e) => setForm({ ...form, shop: e.target.value })}
               className="sm:col-span-3 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-emerald-500" />
        <select data-testid="briefing-input-hour" value={form.send_hour}
                onChange={(e) => setForm({ ...form, send_hour: parseInt(e.target.value, 10) })}
                className="sm:col-span-2 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-emerald-500">
          {Array.from({ length: 24 }).map((_, i) => (
            <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>
          ))}
        </select>
        <select data-testid="briefing-input-tz" value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="sm:col-span-2 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-emerald-500">
          {tzList.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </select>
        <button data-testid="briefing-add" onClick={subscribe} disabled={saving}
                className="sm:col-span-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60 transition">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      {error && <div data-testid="briefing-error" className="text-xs text-rose-600 mb-4 -mt-2">{error}</div>}

      {/* List */}
      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : subs.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No briefing subscribers yet. Add one above to start the 7 AM cadence.</div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold">
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left hidden md:table-cell">Shop</th>
                <th className="px-3 py-2 text-left"><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />Hour</span></th>
                <th className="px-3 py-2 text-left hidden sm:table-cell"><span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />Timezone</span></th>
                <th className="px-3 py-2 text-left">Last status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {subs.map((s) => (
                <tr key={s.id} data-testid={`briefing-row-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-900 font-medium truncate max-w-[200px]">{s.email}</td>
                  <td className="px-3 py-2 text-slate-500 hidden md:table-cell truncate max-w-[180px]">{s.shop || "—"}</td>
                  <td className="px-3 py-2 text-slate-700 tabular-nums">{String(s.send_hour ?? s.send_hour_utc ?? 7).padStart(2, "0")}:00</td>
                  <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{s.timezone || "UTC"}</td>
                  <td className="px-3 py-2"><StatusPill status={s.last_status} active={s.active} lastSent={s.last_sent_at} testStatus={testStatus[s.id]} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`briefing-toggle-${s.id}`} onClick={() => updateSub(s.id, { active: !s.active })}
                            title={s.active ? "Pause" : "Resume"}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 text-slate-600 transition">
                      {s.active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <button data-testid={`briefing-test-${s.id}`} onClick={() => sendTest(s)}
                            title="Send test email now"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 text-emerald-600 transition">
                      <Send className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`briefing-delete-${s.id}`} onClick={() => deleteSub(s.id)}
                            title="Delete"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-rose-50 text-rose-500 transition">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const StatusPill = ({ status, active, lastSent, testStatus }) => {
  const t = testStatus;
  if (t === "sending") return <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Loader2 className="w-3 h-3 animate-spin" /> sending…</span>;
  if (t === "sent")    return <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> test sent</span>;
  if (t?.startsWith?.("failed")) return <span className="inline-flex items-center gap-1 text-xs text-rose-600"><XCircle className="w-3.5 h-3.5" /> {t.replace("failed: ", "")}</span>;
  if (!active)         return <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Pause className="w-3 h-3" /> paused</span>;
  if (status === "sent") {
    const when = lastSent ? new Date(lastSent).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> sent · {when}</span>;
  }
  if (status) return <span className="inline-flex items-center gap-1 text-xs text-rose-600"><XCircle className="w-3.5 h-3.5" /> {String(status).slice(0, 32)}</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-slate-400">awaiting first send</span>;
};
